<template>
  <div class="v2-card v2-dashboard-brands">
    <div class="v2-card-header">
      <h3 class="v2-heading-sm">
        Brands
      </h3>
    </div>

    <div
      v-if="isLoadedAllBrands"
      class="v2-card-body"
    >
      <div
        v-if="brands.length"
        class="v2-brands-grid"
      >
        <button
          v-for="(brand, i) in brands"
          :key="`${brand._id}-${i}`"
          class="v2-brand-tile"
          type="button"
          :aria-label="brand.name"
          @click="goToBrandDetail(brand)"
        >
          <img
            v-if="brand.logo"
            :src="brand.logo"
            :alt="brand.name"
            class="v2-brand-logo"
            @error="brand.logo = null"
          >
          <div
            v-else
            class="v2-brand-logo-fallback"
            :style="{ backgroundColor: brandAvatarColor(brand.name) }"
          >
            {{ (brand.name || '').charAt(0).toUpperCase() }}
          </div>
          <span class="v2-brand-overlay">
            <span class="v2-brand-overlay-name">{{ brand.name }}</span>
          </span>
        </button>
      </div>

      <div
        v-else
        class="v2-empty-state"
      >
        <div class="v2-empty-icon">
          <feather-icon
            icon="SearchIcon"
            size="22"
          />
        </div>
        <p class="v2-empty-text v2-text-sm">
          There are no brands that have invited you.
        </p>
        <router-link
          :to="{ name: 'connections' }"
          class="v2-empty-cta"
        >
          Go to My Brands
        </router-link>
      </div>
    </div>

    <div
      v-else
      class="v2-loading"
    >
      <div
        class="v2-spinner"
        aria-label="Loading"
      />
    </div>
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import { getAvatarColor } from '@/utils/avatar-utils'
import constants from '@/constants'
import { useDashboardBrands } from '@/composables/useDashboardBrands'

export default {
  name: 'V2DashboardBrandsList',
  setup() {
    const page = useDashboardBrands()
    return { ...page }
  },
  mounted() {
    this.loadBrands()
  },
  methods: {
    brandAvatarColor(name) {
      return getAvatarColor(name)
    },
    goToBrandDetail(brand) {
      this.$router.push({
        name: 'v2-brand/detail',
        params: { brandId: brand._id },
      })
      analytics.track(
        constants.TRACKS.ACTIONS.USER_CLICKS_A_BRAND_ON_DASHBOARD,
        { brand, profile: this.profile },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-dashboard-brands {
  display: flex;
  flex-direction: column;
}

.v2-card-header {
  padding: 1.25rem 1.25rem 0;
}

.v2-card-body {
  padding: 1rem 1.25rem 1.25rem;
}

.v2-brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 200px);
  gap: 1rem;
  justify-content: start;
}

.v2-brand-tile {
  position: relative;
  aspect-ratio: 1 / 1;
  padding: 0;
  border: 1px solid var(--v2-border);
  border-radius: 10px;
  background: var(--v2-card);
  cursor: pointer;
  overflow: hidden;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.v2-brand-tile:hover,
.v2-brand-tile:focus-visible {
  border-color: var(--v2-primary);
  box-shadow: 0 1px 3px rgba(116, 125, 238, 0.15);
  outline: none;
}

.v2-brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.75rem;
  background: var(--v2-card);
}

.v2-brand-logo-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Host Grotesk', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
}

.v2-brand-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.v2-brand-tile:hover .v2-brand-overlay,
.v2-brand-tile:focus-visible .v2-brand-overlay {
  opacity: 1;
}

.v2-brand-overlay-name {
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  text-align: center;
  text-transform: capitalize;
  padding: 0 0.5rem;
  word-break: break-word;
}

.v2-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.v2-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--v2-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--v2-muted-foreground);
}

.v2-empty-text {
  margin: 0;
  color: var(--v2-muted-foreground);
}

.v2-empty-cta {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: var(--v2-primary);
  color: var(--v2-primary-foreground);
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  transition: opacity 0.15s;
}

.v2-empty-cta:hover,
.v2-empty-cta:focus-visible {
  opacity: 0.9;
  color: var(--v2-primary-foreground);
  text-decoration: none;
  outline: none;
}

.v2-loading {
  padding: 2rem;
  display: flex;
  justify-content: center;
}

.v2-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--v2-border);
  border-top-color: var(--v2-primary);
  border-radius: 50%;
  animation: v2-spin 0.8s linear infinite;
}

@keyframes v2-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .v2-brands-grid { grid-template-columns: 1fr; }
}
</style>
